import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Select, Table } from 'antd';
import Checkbox, { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FloatingActionBar, InfiniteScroll, SearchInput, Spin } from 'common/components';
import { getFilterOptions, showMessage, showSuccessMessage } from 'common/helpers';
import { useInventoryContext } from 'common/context';
import {
  DEFAULT_EMPTY_VALUE,
  DEFAULT_LIST_OFFSET,
  DEFAULT_PAGINATION_PAGE,
  DEFAULT_VALUE_0,
  LIST_LIMIT_0,
  LIST_LIMIT_20,
  SELECT_LIST_HEIGHT_320,
} from 'common/config';
import { ArrowDownShort, Bin, Cancelled, Tick } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { InventoryActionCard, InventoryEmptyList } from 'entities/Inventory/components';
import { EInventoryActionStatus, EInventoryActionType } from 'entities/Inventory/Inventory.enum';
import { renderInventoryActionListRecords } from 'entities/Inventory/Inventory.helper';
import { IInventoryAction, IInventoryActionListPayload } from 'entities/InventoryAction/InventoryAction.models';
import {
  deleteInventoryAction,
  filterInventoryActionList,
  getInventoryActionById,
  getInventoryActionList,
  getInventoryActionListBatch,
  getInventoryActionListUsers,
  selectInventoryActionCount,
  selectInventoryActionList,
  selectInventoryActionListUsers,
  selectIsInventoryActionLoading,
  setInventoryActionList,
  updateInventoryAction,
} from 'entities/InventoryAction/InventoryAction.slice';
import { getUserInventoryStats } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  menu: ReactNode;
}

export const IncomingWriteOffInventoryActionListTab: FC<IComponentProps> = ({ header, menu }) => {
  const dispatch = useAppDispatch();

  const inventoryActionList = useAppSelector(selectInventoryActionList);
  const inventoryActionCount = useAppSelector(selectInventoryActionCount);
  const isInventoryActionLoading = useAppSelector(selectIsInventoryActionLoading);
  const inventoryActionListUsers = useAppSelector(selectInventoryActionListUsers);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [selectedInventoryActionList, setSelectedInventoryActionList] = useState<IInventoryAction[]>([]);
  const [payload, setPayload] = useState<IInventoryActionListPayload>({
    types: [EInventoryActionType.WriteOff],
    statuses: [EInventoryActionStatus.New],
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const { setOpenInventoryActionCard } = useInventoryContext();

  const checkAll = inventoryActionList.length === selectedInventoryActionList.length;
  const indeterminate =
    selectedInventoryActionList.length > DEFAULT_VALUE_0 && selectedInventoryActionList.length < inventoryActionList.length;
  const userOptions = getFilterOptions.user(inventoryActionListUsers);

  const onUserChange = (value: number) => {
    const newPayload = { ...payload, userId: value === DEFAULT_EMPTY_VALUE ? undefined : value };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    dispatch(getInventoryActionList(newPayload));
  };

  const onSearchClick = async (value?: string) => {
    const newPayload = { ...payload, search: value };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    await dispatch(getInventoryActionList(newPayload));
  };

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    e.target.checked ? setSelectedInventoryActionList(inventoryActionList) : setSelectedInventoryActionList([]);
  };

  const onInventoryActionSelectionChange = (id: number, checked: boolean) => {
    const action = inventoryActionList.find((item) => item.id === id) as IInventoryAction;
    const newSelectedInventoryActionList = checked
      ? [...selectedInventoryActionList, action]
      : selectedInventoryActionList.filter((item) => item.id !== id);

    setSelectedInventoryActionList(newSelectedInventoryActionList);
  };

  const onChangeRequestStatusSuccess = async (records: IInventoryAction[]) => {
    const recordIds = records.map((record) => record.id);

    await dispatch(getUserInventoryStats({}));
    dispatch(filterInventoryActionList(records));
    setOpenInventoryActionCard(false);

    const filteredInventoryActionList = inventoryActionList.filter((action) => !recordIds.includes(action.id));

    if (filteredInventoryActionList.length === LIST_LIMIT_0) {
      const newPayload = { ...payload, offset: DEFAULT_LIST_OFFSET };

      setPage(DEFAULT_PAGINATION_PAGE);
      setPayload(newPayload);
      dispatch(getInventoryActionList(newPayload));
    }
  };

  const onAcceptRequestClick = (records: IInventoryAction[]) => {
    const recordIds = records.map((record) => record.id);
    const recordsCount = records.reduce((acc, record) => acc + record.count, 0);

    dispatch(
      updateInventoryAction({
        ids: recordIds,
        onSuccess: () => {
          showSuccessMessage(`${recordsCount} запросов о списании оборудования принято.`);
          onChangeRequestStatusSuccess(records);
        },
      }),
    );
  };

  const onCancelRequestClick = (records: IInventoryAction[]) => {
    const recordIds = records.map((record) => record.id);
    const recordsCount = records.reduce((acc, record) => acc + record.count, 0);

    dispatch(
      deleteInventoryAction({
        ids: recordIds,
        onSuccess: async () => {
          showMessage({
            content: `${recordsCount} запросов о списании оборудования отклонено.`,
            icon: <Bin className="icon-bin-dark-grey" />,
          });
          onChangeRequestStatusSuccess(records);
        },
      }),
    );
  };

  useEffect(() => {
    const fetch = async () => {
      dispatch(setInventoryActionList([]));
      await dispatch(getUserInventoryStats({}));
      await dispatch(
        getInventoryActionListUsers({ type: EInventoryActionType.WriteOff, statuses: [EInventoryActionStatus.New] }),
      );
      await dispatch(getInventoryActionList(payload));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <>
      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isInventoryActionLoading && inventoryActionList.length < inventoryActionCount}
        loader={<Spin />}
        scrollToTopTrigger={[payload]}
        loadMore={async () => {
          setScrollLoading(true);
          setPage(page + 1);
          await dispatch(
            getInventoryActionListBatch({
              ...payload,
              offset: LIST_LIMIT_20 * page,
            }),
          );
          setScrollLoading(false);
        }}
      >
        {header}
        {menu}

        <div className="inventory-list__filters flex mb-12">
          <Select
            rootClassName="redesign"
            className="inventory-list__filters-select"
            options={userOptions}
            value={payload?.userId !== undefined ? payload.userId : DEFAULT_EMPTY_VALUE}
            onChange={onUserChange}
            placeholder="Выберите пользователя"
            listHeight={SELECT_LIST_HEIGHT_320}
            virtual={false}
            suffixIcon={<ArrowDownShort />}
          />

          <SearchInput placeholder="Искать по названию или артикулу" onSearch={onSearchClick} />
        </div>

        {!!inventoryActionList.length && (
          <div className="sticky px-3 pb-4 flex align-center inventory-list__actions">
            <Checkbox checked={checkAll} indeterminate={indeterminate} onChange={onCheckAllChange}>
              Все запросы
            </Checkbox>
          </div>
        )}

        <Table
          className={`
                table-hover
                ${!!inventoryActionList.length && inventoryActionList.length === inventoryActionCount && 'table-end-of-list'}
                ${!inventoryActionList.length && 'table-empty'}
              `}
          dataSource={inventoryActionList.map((inventory) => ({ ...inventory, key: inventory.id }))}
          columns={renderInventoryActionListRecords({ isIncoming: true, onAcceptRequestClick, onCancelRequestClick })}
          showHeader={false}
          pagination={false}
          rowSelection={{
            selectedRowKeys: selectedInventoryActionList.map((action) => action.id),
            onSelect: ({ id }, checked) => onInventoryActionSelectionChange(id, checked),
            columnWidth: 40,
          }}
          onRow={({ id }) => ({
            onClick: async () => {
              await dispatch(getInventoryActionById(id));
              setOpenInventoryActionCard(true);
            },
          })}
          loading={isInventoryActionLoading}
          locale={{
            emptyText: fetchLoading ? <Spin /> : <InventoryEmptyList title="Активных запросов нет" />,
          }}
        />
      </InfiniteScroll>

      <InventoryActionCard isIncoming onAccept={onAcceptRequestClick} onCancel={onCancelRequestClick} />

      <FloatingActionBar selectedCount={selectedInventoryActionList.length} count={inventoryActionList.length}>
        <Button
          className="floating-action-bar__btn"
          icon={<Tick className="icon-white" />}
          onClick={() => onAcceptRequestClick(selectedInventoryActionList)}
          loading={isInventoryActionLoading}
        >
          Принять все
        </Button>
        <Button
          className="floating-action-bar__btn red"
          icon={<Cancelled className="icon-red" />}
          onClick={() => onCancelRequestClick(selectedInventoryActionList)}
          loading={isInventoryActionLoading}
        >
          Отклонить
        </Button>
      </FloatingActionBar>
    </>
  );
};
